import { Routes, Navigate } from 'react-router-dom';

import { Typography, ConfirmDialog, CircularProgress } from '@occc/ui-design-system';
import { OfferSupportAlert, useAppLayoutContextConsumer, Page } from '@occc-app/design-system';
import { SentryRoute as Route } from '@occc-app/App/sentry';
import {
  ConfigurationLayout,
  CampaignComponentTabs,
} from '@occc-app/routes/CampaignComponents/components/ConfigurationLayout';

import { OfferConfigurePageProps } from './OfferConfigurePage';

import ScheduleForm from '../ScheduleForm';
import TargetForm from '../TargetForm';
import DesignForm from '../DesignForm';

export const OfferUpdatePage = ({
  layoutProps,
  formContainersProps,
  dialogProps,
  scheduleFormProps,
  configureForm,
  pageTitle,
  subtitle,
  isDesignTabHidden,
  targetTabProps,
  showExternalUrl,
  showSurgeonGeneralsWarning,
}: OfferConfigurePageProps) => {
  useAppLayoutContextConsumer(layoutProps.breadcrumbLinks);
  const resolvedTargetTabProps =
    typeof targetTabProps === 'function'
      ? targetTabProps(formContainersProps.offer)
      : targetTabProps;

  const renderPageContent = () => {
    if (!layoutProps.campaignComponentType) {
      return (
        <OfferSupportAlert
          title='Command Center does not yet support this Campaign component type.'
          fallbackLink={`/offers/${layoutProps.offerId}`}
          fallbackLinkTitle='View the original offer'
        />
      );
    }

    return (
      <ConfigurationLayout
        {...layoutProps}
        isDesignTabHidden={isDesignTabHidden}
        onGetTabLabel={(item) =>
          item.value === CampaignComponentTabs.Target && resolvedTargetTabProps?.tabLabel
            ? resolvedTargetTabProps.tabLabel
            : item.label
        }
      >
        <Routes>
          <Route path='' element={<Navigate to={CampaignComponentTabs.Configure} replace />} />

          <Route path={CampaignComponentTabs.Configure} element={configureForm} />

          <Route
            path={CampaignComponentTabs.Target}
            element={
              <TargetForm
                {...formContainersProps}
                isDesignTabHidden={isDesignTabHidden}
                targetTabProps={targetTabProps}
              />
            }
          />

          {!isDesignTabHidden && (
            <Route
              path={CampaignComponentTabs.Design}
              element={
                <DesignForm
                  {...formContainersProps}
                  showExternalUrl={showExternalUrl}
                  showSurgeonGeneralsWarning={showSurgeonGeneralsWarning}
                />
              }
            />
          )}

          <Route
            path={CampaignComponentTabs.Schedule}
            element={<ScheduleForm {...scheduleFormProps} />}
          />
        </Routes>
      </ConfigurationLayout>
    );
  };

  return (
    <Page
      browserTabTitle={pageTitle}
      containerProps={{ variant: 'md' }}
      pageHeaderProps={{ title: layoutProps.title || pageTitle, subtitle }}
    >
      {layoutProps.loading ? <CircularProgress /> : renderPageContent()}
      <ConfirmDialog
        dialogTitle={`Cancel ${dialogProps.offerTypeTitle} Update`}
        open={dialogProps.open}
        confirmButtonText='Yes'
        confirmButtonProps={{
          onClick: dialogProps.onConfirmClick,
        }}
        onClose={dialogProps.closeModal}
      >
        <Typography>
          {`Wait! If you continue, this ${dialogProps.offerTypeTitle} will be not updated and your progress will be lost. Do you still want to cancel?`}
        </Typography>
      </ConfirmDialog>
    </Page>
  );
};
